"use client";

import { useActionState } from "react";

import Link from "next/link";
import { AtSign } from "lucide-react";

import { Separator } from "@/components/ui/separator";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import KeyIcon from "@/components/icons/Key";
import GoogleIcon from "@/components/icons/GoogleIcon";
// import FacebookIcon from '@/components/icons/FacebookIcon';
// import AppleIcon from '@/components/icons/AppleIcon';

import { resendConfirmationEmail, signIn } from "@/lib/actions/auth";
import { createClient } from "@/lib/supabase/client";
import { P, Span } from "@/components/ui/typography";
import { useRouter, useSearchParams } from "next/navigation";

const supabase = createClient();

export function LoginForm() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const message = searchParams.get("message");
  const email_from_search = searchParams.get("email");

  const [state, formAction, isPending] = useActionState(signIn, {
    error: undefined,
    showResendConfirmationEmail: undefined,
  });

  const [resendState, resendFormAction, resendIsPending] = useActionState(resendConfirmationEmail, {
    error: null,
    success: false,
  });

  async function handleOAuthLogin(provider: "facebook" | "google" | "apple") {
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
        queryParams: {
          prompt: "select_account",
        },
      },
    });
  }

  return (
    <form className="flex flex-col gap-4 w-full md:max-w-[26rem]" action={formAction}>
      {/* <Alert type="warning" className="text-base"> */}
      {/*   <P>¡Aún no puedes entrar a Creators Quest, explorador!</P> */}
      {/*   <P className="!mt-0"> */}
      {/*     Pero no entres en pánico ¡Las puertas del Quest se abrirán muy pronto! Mientras esperas, */}
      {/*     síguenos en @creatorsquestbyfede para no perderte ninguna pista. ¡La aventura está a punto */}
      {/*     de comenzar! */}
      {/*   </P> */}
      {/* </Alert> */}

      <div className="flex flex-col gap-4">
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Correo electrónico"
          defaultValue={state.fields?.email}
          autoFocus
          icon={<AtSign />}
          required
        />
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder="Contraseña"
          icon={<KeyIcon />}
          required
        />

        {message === "success_password_update" && (
          <Alert type="success">
            <p className="font-secondary text-base">
              Tu contraseña se ha actualizado correctamente. Ahora puedes iniciar sesión.
            </p>
          </Alert>
        )}

        {message === "email_confirmation_pending" && (
          <Alert type="success">
            <p className="font-secondary text-base">
              Hemos enviado un correo electrónico de confirmación a{" "}
              <strong>{email_from_search}</strong>. Por favor revise su correo electrónico y haga
              click en el enlace para verificar su cuenta y continuar con la inscripción.
            </p>
          </Alert>
        )}

        {state?.error && !resendState?.success && !resendState?.error && (
          <Alert type="error">{state.error}</Alert>
        )}

        {resendState?.error && <Alert type="error">{resendState.error}</Alert>}

        {resendState?.success && (
          <Alert type="success">
            <p className="font-secondary text-base">
              Hemos enviado un correo electrónico de confirmación a{" "}
              <strong>{state.fields?.email}</strong>. Por favor revise su correo electrónico y haga
              click en el enlace para verificar su cuenta y continuar con la inscripción.
            </p>
          </Alert>
        )}

        {state?.showResendConfirmationEmail && !resendState?.success && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={resendIsPending}
              onClick={() => {
                if (state.fields?.email) {
                  const formData = new FormData();

                  formData.append("email", state.fields?.email);

                  resendFormAction(formData);
                }
              }}
            >
              Reenviar confirmación
            </Button>
          </div>
        )}

        <div className="flex items-center justify-center mt-2">
          <Link href="/forgot-password" className="text-link">
            Olvidé mi contraseña
          </Link>
        </div>
      </div>

      <div className="flex justify-center mt-4">
        <Button
          type="submit"
          className="w-full"
          loading={isPending}
          onClick={() => {
            router.replace("/login", { scroll: false });
          }}
        >
          Ingresar
        </Button>
      </div>

      <div className="flex justify-center relative text-center mt-12">
        <Separator className="bg-black" />
        <Span className="absolute top-1/2 -translate-y-1/2 bg-white px-4 !text-lg">
          Ingresa por medio de
        </Span>
      </div>

      <div className="flex justify-around items-center gap-4 mt-2">
        <button
          type="button"
          onClick={() => handleOAuthLogin("google")}
          className="p-3 border-transparent border-2 hover:border-accent hover:border-2 rounded-full"
        >
          <GoogleIcon className="w-8" />
        </button>
        {/* <Button */}
        {/*   type="button" */}
        {/*   variant="ghost" */}
        {/*   size="icon" */}
        {/*   onClick={() => handleOAuthLogin('facebook')} */}
        {/* > */}
        {/*   <FacebookIcon className="w-8" /> */}
        {/* </Button> */}
        {/* <Button type="button" variant="ghost" size="icon"> */}
        {/*   <AppleIcon className="w-9" /> */}
        {/* </Button> */}
      </div>

      {/* <div className="flex justify-center gap-4 text-center"> */}
      {/*   <span className="font-medium">¿Aún no tienes una cuenta?</span> */}
      {/*   <Link href="/signup" className="font-medium text-link hover:text-accent/80"> */}
      {/*     Inscríbete */}
      {/*   </Link> */}
      {/* </div> */}
    </form>
  );
}
